import { InformationCircleIcon } from '@heroicons/react/24/outline';
import { isEqual } from 'lodash';
import { useEffect, useMemo, useState } from 'react';
import { FormProvider, useController, useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';

import ConditionalRenderer from '@/components/ui/custom/ConditionalRenderer';
import { Alert, TextInput, ToggleGroup } from '@/components/ui/daisy';
import Fieldset from '@/components/ui/daisy/fieldset';
import Exam, { ExamDTO, ExamTypeEnum } from '@/models/Exam';
import { Nullable } from '@/models/Model';
import { examToFields } from '@/utils/exam/examToFields';

import AssessmentItemFields from './AssessmentItemFields';
import ExamFormControls from './ExamFormControls';
import { examFieldsToDTO } from '@/utils/exam/examFieldsToDTO';
import { BaseExamCoursePath } from '@/data/hook/exam/useExams';

export type ExamFormFields = Pick<
  ExamDTO,
  'name' | 'type' | 'defaultTime' | 'assessmentItemId'
> & {
  description: string;
  assessmentId: Nullable<number>;
};

export type ExamFormVariant = 'default' | 'inline';

type ExamFormProps = {
  exam?: Exam;
  coursePath: BaseExamCoursePath;
  courseLineId: number;
  isLoading?: boolean;
  onSubmit: (data: ExamFormFields) => void;
  onCancel?: () => void;
  variant?: ExamFormVariant;
};

export default function ExamForm({
  exam,
  coursePath,
  courseLineId,
  isLoading,
  onSubmit,
  onCancel,
  variant = 'default',
}: ExamFormProps) {
  const { t } = useTranslation('translation', {
    keyPrefix: 'courseLineForm.examForm.form',
  });
  const { t: tExamTypes } = useTranslation('translation', {
    keyPrefix: 'examTypes',
  });

  const [hasChanges, setHasChanges] = useState(false);

  const defaultValues = useMemo(() => examToFields(exam), [exam]);

  const methods = useForm<ExamFormFields>({ defaultValues });
  const {
    watch,
    control,
    register,
    setValue,
    handleSubmit,
    formState: { errors },
  } = methods;

  const typeValue = watch('type');

  const { field: typeField } = useController({
    control,
    name: 'type',
    rules: { required: t('typeRequired') },
  });

  useEffect(() => {
    const subscription = watch(value => {
      const persisted = examFieldsToDTO(value as ExamFormFields);
      const base = examFieldsToDTO(defaultValues);
      setHasChanges(!isEqual(persisted, base));
    });
    return () => {
      setHasChanges(false);
      subscription.unsubscribe();
    };
  }, [defaultValues, watch]);

  useEffect(() => {
    if (typeValue !== ExamTypeEnum.SIMULATED) return;
    setValue('assessmentId', null);
    setValue('assessmentItemId', null);
  }, [typeValue, setValue]);

  return (
    <FormProvider {...methods}>
      <form className="flex flex-col gap-6" onSubmit={handleSubmit(onSubmit)}>
        <TextInput.Fieldset label={t('name')} labelAlt={errors?.name?.message}>
          <TextInput
            type="text"
            placeholder={t('namePlaceholder')}
            className="w-full"
            color={!errors?.name?.message ? 'default' : 'error'}
            {...register('name', { required: t('nameRequired') })}
          />
        </TextInput.Fieldset>

        <TextInput.Fieldset label={t('description')}>
          <TextInput
            type="text"
            placeholder={t('descriptionPlaceholder')}
            className="w-full"
            {...register('description')}
          />
        </TextInput.Fieldset>

        <Fieldset label={t('type')} labelAlt={errors?.type?.message}>
          <ToggleGroup value={typeField.value} onChange={typeField.onChange}>
            <ToggleGroup.Item
              variant="dash"
              color="secondary"
              value={ExamTypeEnum.TEST}
            >
              {tExamTypes(ExamTypeEnum.TEST)}
            </ToggleGroup.Item>
            <ToggleGroup.Item
              variant="dash"
              color="secondary"
              value={ExamTypeEnum.SIMULATED}
            >
              {tExamTypes(ExamTypeEnum.SIMULATED)}
            </ToggleGroup.Item>
          </ToggleGroup>
        </Fieldset>

        <ConditionalRenderer condition={typeValue === ExamTypeEnum.TEST}>
          <AssessmentItemFields
            coursePath={coursePath}
            courseLineId={courseLineId}
            currentExamId={exam?.id ?? null}
          />
        </ConditionalRenderer>

        <ConditionalRenderer condition={typeValue === ExamTypeEnum.SIMULATED}>
          <Alert variant="soft">
            <InformationCircleIcon className="w-6 h-6 shrink-0 text-info" />
            <span className="text-xs">{t('simulatedNoAssessmentInfo')}</span>
          </Alert>
        </ConditionalRenderer>

        <ExamFormControls
          variant={variant}
          isLoading={isLoading}
          onCancel={onCancel}
          hasChanges={hasChanges}
        />
      </form>
    </FormProvider>
  );
}
